import { api } from './client';
import type { TripPhoto } from '../types';
import type { PhotoCaptureFields } from '../utils/preparePhoto';

/** ADR-0058: a stop of the photo's candidates, or none. Never applied by the server. */
export interface StopSuggestion {
  photoId: string;
  activityId: string | null;
  dayId: string | null;
  confidence: 'high' | 'low' | null;
}

/** One AI credit buys one request of up to this many photos (ADR-0058). */
export const MAX_PHOTOS_PER_SUGGESTION = 10;

export const photosApi = {
  list: (tripId: string) =>
    api.get<TripPhoto[]>(`/trips/${tripId}/photos`).then(r => r.data),
  upload: (
    tripId: string,
    file: File,
    dayId?: string | null,
    capture: PhotoCaptureFields = {},
    onProgress?: (pct: number) => void,
  ) => {
    const form = new FormData();
    form.append('file', file);
    if (dayId) form.append('dayId', dayId);
    // ADR-0042: a photo shrunk in the browser lost its EXIF; these place it.
    for (const [key, value] of Object.entries(capture)) form.append(key, value);
    return api.post<TripPhoto>(
      `/trips/${tripId}/photos`,
      form,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: e => onProgress?.(Math.round((e.loaded / (e.total ?? e.loaded)) * 100)),
      },
    ).then(r => r.data);
  },
  /** Moving to another day drops the stop and the diary entry unless a new stop comes along. */
  update: (tripId: string, photoId: string, patch: { dayId?: string | null; activityId?: string | null }) =>
    api.patch<TripPhoto>(`/trips/${tripId}/photos/${photoId}`, patch).then(r => r.data),
  suggestStops: (tripId: string, photoIds: string[]) =>
    api.post<{ suggestions: StopSuggestion[]; aiCreditsLeft: number; premiumAiCreditsLeft: number }>(
      `/trips/${tripId}/photos/suggest-stops`, { photoIds },
    ).then(r => r.data),
  remove: (tripId: string, photoId: string) =>
    api.delete(`/trips/${tripId}/photos/${photoId}`),
};
